import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, signal } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { ApiError, toInteger } from 'src/app/shared/model/utility';
import { SB_PANEL_MESSAGE, SnackbarService } from 'src/app/shared/snackbar.service';
import { FindUsageEventsWithAggregationsQuery, TenantContractLimitModel, UsageEventsApi } from 'src/app/usage-api';

interface UsageType {
  category: string;
  code: string;
  current: number;
  limit: number;
  priority: string;
  descriptor?: string;
}

@Component({
  selector: 'app-contract-limit-overview',
  templateUrl: './contract-limit-overview.component.html',
  styleUrls: ['./contract-limit-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ContractLimitOverviewComponent implements OnInit, OnChanges {
  @Input() limits: TenantContractLimitModel[] = [];
  @Input() contractId: string = '';
  @Input() tenantId: string = '';
  @Input() startDate: Date = new Date();
  @Input() endDate: Date = new Date();
  usages = signal<UsageType[]>([]);

  constructor(
    private readonly usageEventsApi: UsageEventsApi,
    private readonly sb: SnackbarService,
  ) {}

  ngOnInit(): void {
    if (this.limits && this.limits?.length > 0) {
      this.usages.set([]);
      this.usages.set(
        <UsageType[]>this.limits?.map((l) => ({
          category: l.category,
          code: l.code,
          limit: l.limitation,
          priority: l.priority,
          current: 0,
          percentUsed: 0,
          descriptor: l.priority != '' ? l.category + ': ' + l.code + ' - ' + l.priority : l.category + ': ' + l.code,
        })),
      );
    }

    this.usages().forEach((item, itemIndex) => {
      this.loadUsage(item, itemIndex);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.startDate || changes.endDate) {
      this.usages().forEach((item, itemIndex) => {
        this.loadUsage(item, itemIndex);
      });
    }
  }

  private loadUsage(item: UsageType, itemIndex: number) {
    const query = this.buildAggregationQuery(item.category, item.code, item.priority, this.startDate, this.endDate);
    this.usageEventsApi
      .usageEventsAggregateGridUsageEvents(this.tenantId, 0, 1, 25, query)
      .pipe(
        map((r) => {
          let foundMatch = false;
          
          r.rows?.forEach((row) => {
            let resultCategory: string = '';
            let resultCode: string = '';
            let resultPriority: string = '';

            if (item.category && item.category != '') {
              const index = r?.columns?.indexOf('category');
              if (index !== undefined && index !== -1) {
                resultCategory = row[index] ?? '';
              }
            }

            if (item.code && item.code != '') {
              const index = r?.columns?.indexOf('code');
              if (index !== undefined && index !== -1) {
                resultCode = row[index] ?? '';
              }
            }

            if (item.priority && item.priority != '') {
              const index = r?.columns?.indexOf('source.priority');
              if (index !== undefined && index !== -1) {
                resultPriority = row[index] ?? '';
              }
            }

            if (resultCategory == item.category && resultCode == item.code && resultPriority == item.priority) {
              const index = r?.columns?.indexOf('Sum(usage.transactions)');
              if (index !== undefined && index !== -1) {
                const currentUsage = toInteger(row[index] ?? '');
                foundMatch = true;

                this.usages.update((usages) =>
                  usages.map((usage, index) => (index === itemIndex ? { ...usage, current: currentUsage } : usage)),
                );
              }
            }
          });

          // If no matching data was found, reset to 0
          if (!foundMatch) {
            this.usages.update((usages) =>
              usages.map((usage, index) => (index === itemIndex ? { ...usage, current: 0 } : usage)),
            );
          }
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error getting aggregation for ' + item.category + ': ' + item.code);
          return of(null);
        }),
      )
      .subscribe();
  }

  buildAggregationQuery(
    category: string,
    code: string,
    priority: string,
    startDate: Date,
    endDate: Date,
  ): FindUsageEventsWithAggregationsQuery {
    const aggregations: string[] = [];

    if (category && category !== '') {
      aggregations.push('category');
    }

    if (priority && priority !== '') {
      aggregations.push('source.priority');
    }

    if (code && code !== '') {
      aggregations.push('code');
    }

    aggregations.push('Sum(usage.transactions)');

    const query: FindUsageEventsWithAggregationsQuery = {
      aggregations: aggregations,
      category: category,
      code: code,
      priority: priority,
      dateRange: { startDate: startDate, endDate: endDate },
    };

    return query;
  }

  accentColor(usage: UsageType): string {
    const value = usage.current / usage.limit;
    if (value < 0.5) {
      return 'primary';
    } else if (value < 0.7) {
      return 'accent';
    } else {
      return 'warn';
    }
  }

  renderLimitLabel(usage: UsageType): string {
    if (usage.limit == 0) {
      return `${usage.current}`;
    } else {
      return `${usage.current} / ${usage.limit}`;
    }
  }

  displayErrorMessage(message: string) {
    this.sb.open(message, undefined, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }
}
